import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent
} from "react";

/**
 * Drag-to-reposition for the in-call card and pill (CRM-1112).
 *
 * Both surfaces keep their `fixed bottom-4 right-4` anchor and are moved with a
 * translate on top of it, so the offset means the same thing for either one:
 * collapsing the card to the pill keeps the bottom-right corner where the user
 * dropped it. Reported by a partner who couldn't reach the Inbox Send button
 * while on a call.
 */
export interface DragOffset {
  x: number;
  y: number;
}

export const ZERO_DRAG_OFFSET: DragOffset = { x: 0, y: 0 };

// Matches the `bottom-4 right-4` anchor both surfaces render at.
const ANCHOR_PX = 16;
// Smallest gap we let the user leave between the surface and the viewport edge.
const EDGE_MARGIN_PX = 8;
// Pointer travel before a press turns into a drag, so taps still hit buttons.
const DRAG_THRESHOLD_PX = 4;

/**
 * Keep one axis on screen. At offset 0 the far edge sits `ANCHOR_PX` from the
 * viewport edge, so moving towards the anchor is capped at `ANCHOR_PX -
 * EDGE_MARGIN_PX` and moving away is capped once the opposite edge reaches the
 * margin. A surface taller/wider than the viewport clamps back to the anchor.
 */
const clampAxis = (offset: number, size: number, viewport: number) =>
  Math.min(
    Math.max(offset, size + ANCHOR_PX + EDGE_MARGIN_PX - viewport),
    ANCHOR_PX - EDGE_MARGIN_PX
  );

const clampOffset = (
  offset: DragOffset,
  node: HTMLElement | null
): DragOffset => {
  if (!node) return offset;
  const { width, height } = node.getBoundingClientRect();
  const x = clampAxis(offset.x, width, window.innerWidth);
  const y = clampAxis(offset.y, height, window.innerHeight);
  // Hand back the same reference when nothing moved, so the mount-time and
  // resize-time re-checks don't churn state for a no-op.
  if (x === offset.x && y === offset.y) return offset;
  return { x, y };
};

const dragCursorClass = (isDragging: boolean) =>
  isDragging ? "cursor-grabbing select-none" : "cursor-grab";

interface DragOrigin {
  pointerX: number;
  pointerY: number;
  offsetX: number;
  offsetY: number;
}

interface UseDraggableOverlayOptions {
  offset: DragOffset;
  onOffsetChange: (offset: DragOffset) => void;
}

export function useDraggableOverlay({
  offset,
  onOffsetChange
}: UseDraggableOverlayOptions) {
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const originRef = useRef<DragOrigin | null>(null);
  // Set once a press crosses the threshold; read by the click handler to
  // swallow the click that a drag would otherwise fire on a control.
  const draggedRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);

  const offsetRef = useRef(offset);
  offsetRef.current = offset;

  const reclamp = useCallback(
    () => onOffsetChange(clampOffset(offsetRef.current, nodeRef.current)),
    [onOffsetChange]
  );

  // The card and the pill share an offset but not a size, so re-check it
  // whenever the mounted surface changes or the window resizes.
  useLayoutEffect(reclamp, [reclamp]);

  useEffect(() => {
    window.addEventListener("resize", reclamp);
    return () => window.removeEventListener("resize", reclamp);
  }, [reclamp]);

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;
      draggedRef.current = false;
      originRef.current = {
        pointerX: event.clientX,
        pointerY: event.clientY,
        offsetX: offsetRef.current.x,
        offsetY: offsetRef.current.y
      };
    },
    []
  );

  const handlePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const origin = originRef.current;
      if (!origin) return;

      const deltaX = event.clientX - origin.pointerX;
      const deltaY = event.clientY - origin.pointerY;

      if (!draggedRef.current) {
        if (
          Math.abs(deltaX) < DRAG_THRESHOLD_PX &&
          Math.abs(deltaY) < DRAG_THRESHOLD_PX
        )
          return;
        draggedRef.current = true;
        setIsDragging(true);
        // Only capture once it is a real drag: capturing on every press would
        // retarget the click that a plain tap on mute/hang-up needs.
        event.currentTarget.setPointerCapture(event.pointerId);
      }

      onOffsetChange(
        clampOffset(
          { x: origin.offsetX + deltaX, y: origin.offsetY + deltaY },
          nodeRef.current
        )
      );
    },
    [onOffsetChange]
  );

  const handlePointerUp = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.currentTarget.hasPointerCapture(event.pointerId))
        event.currentTarget.releasePointerCapture(event.pointerId);
      originRef.current = null;
      setIsDragging(false);
    },
    []
  );

  // Capture phase: stop the drag's trailing click before it reaches the button
  // underneath, so dropping the pill on top of Hang up doesn't end the call.
  const handleClickCapture = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (!draggedRef.current) return;
      event.preventDefault();
      event.stopPropagation();
    },
    []
  );

  const style: CSSProperties = {
    transform: `translate3d(${offset.x}px, ${offset.y}px, 0)`,
    touchAction: "none"
  };

  return {
    nodeRef,
    dragClassName: dragCursorClass(isDragging),
    style,
    dragProps: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
      onPointerCancel: handlePointerUp,
      onClickCapture: handleClickCapture
    }
  };
}
